import {GltfView} from '../gltf-source/GltfView/gltf_view.js';
import {UIModel} from './logic/uimodel.js';
import {app} from './ui/ui.js';
import {Subject, from, merge} from 'rxjs';
import {mergeMap, filter, map, multicast} from 'rxjs/operators';
import {gltfModelPathProvider, fillEnvironmentWithPaths} from './model_path_provider.js';
import {isNetworkError} from "../gltf-source/gltf/utils";

let sceneCustomDataCache = new Map();

const BASE_MAPPING_NAMES = ['base', 'basecolor', '_col', '_base', 'color'];
const NORMAL_MAPPING_NAMES = ['normal', 'normal', '_nrm'];
const AMBIENT_MAPPING_NAMES = ['ambient', 'occlusion', 'ambientocclusion'];
const METALLIC_ROUGHNESS_TEXTURE_NAME = 'metallicRoughnessTexture';
const BASE_ALPHA_TEXTURE_NAME = "baseAlphaTexture";

const ENVIRONMENT_VARIABLES = window['env'];

async function main() {
    const canvas = document.getElementById("canvas");
    const context = canvas.getContext("webgl2", {alpha: false, antialias: true});
    const view = new GltfView(context);
    const resourceLoader = view.createResourceLoader();
    const state = view.createState();
    state.renderingParameters.useDirectionalLightsWithDisabledIBL = true;

    const pathProvider = new gltfModelPathProvider('assets/models/2.0/model-index.json');
    const alphaPathProvider = new gltfModelPathProvider('assets/models/2.0/model-index-alpha.json');

    await pathProvider.initialize();
    await alphaPathProvider.initialize();
    const environmentPaths = fillEnvironmentWithPaths({"base_environment": "Base environment"}, "assets/environments/");

    const uiModel = new UIModel(app, pathProvider, environmentPaths);
    const queryParams = getQueryParams();

    let glbFileArrayBuffer = null;
    const isGlbFile = isGlbFileMode(queryParams);

    app.setIsGlbFileMode(isGlbFile);
    app.show();

    if (app.isGlbFileMode) {
        uiModel.goToLoadingState();

        try {
            glbFileArrayBuffer = await getGlbFile(queryParams.fileOid, queryParams.env);
        } catch (e) {
            console.log(e);
        }
    }

    // whenever a new model is selected, load it and when complete pass the loaded gltf
    // into a stream back into the UI
    uiModel.goToLoadingState();
    const gltfLoadedSubject = new Subject();
    const gltfLoadedMulticast = uiModel.model.pipe(
        mergeMap((model) => {

            // Workaround for errors in ktx lib after loading an asset with ktx2 files for the second time:
            resourceLoader.initKtxLib();

            return from(getCustomProperties().then((data) => {
                return resourceLoader.loadGltf(glbFileArrayBuffer ? glbFileArrayBuffer : model.mainFile, model.additionalFiles, glbFileArrayBuffer ? {} : data).then((gltf) => {
                    state.gltf = gltf;
                    glbFileArrayBuffer = null;
                    const defaultScene = state.gltf.scene;
                    state.sceneIndex = defaultScene === undefined ? 0 : defaultScene;
                    state.cameraIndex = undefined;
                    if (gltf.nodes[0].camera !== undefined) {
                        state.userCameraIndex = 1;
                    } else {
                        state.userCameraIndex = undefined;
                    }

                    if (state.gltf.scenes.length != 0) {
                        if (state.sceneIndex > state.gltf.scenes.length - 1) {
                            state.sceneIndex = 0;
                        }
                        const scene = state.gltf.scenes[state.sceneIndex];

                        scene.applyTransformHierarchy(state.gltf);
                        if (state.gltf.cameras[0]) {
                            state.userCamera.aspectRatio = state.gltf.cameras[0].aspectRatio;
                            state.userCamera.yfov = state.gltf.cameras[0].yfov;
                            state.userCamera.zfar = state.gltf.cameras[0].zfar;
                            state.userCamera.znear = state.gltf.cameras[0].znear;
                            state.userCamera.name = state.gltf.cameras[0].name;
                        } else {
                            state.userCamera.aspectRatio = canvas.width / canvas.height;
                        }
                        state.userCamera.fitViewToScene(state.gltf, state.sceneIndex, state.userCameraIndex);

                        // Try to start as many animations as possible without generating conficts.
                        state.animationIndices = [];
                        for (let i = 0; i < gltf.animations.length; i++) {
                            if (!gltf.nonDisjointAnimations(state.animationIndices).includes(i)) {
                                state.animationIndices.push(i);
                            }
                        }
                        state.animationTimer.start();
                    }

                    uiModel.exitLoadingState();

                    return state;
                });
            }, (error) => {
                if (isNetworkError(error.message)) {
                    return Promise.reject('Connection error. Please try again later.');
                }

                return Promise.reject(error);
            }));
        }),
        // transform gltf loaded observable to multicast observable to avoid multiple execution with multiple subscriptions
        multicast(gltfLoadedSubject)
    );

    function getCustomProperties() {
        if (sceneCustomDataCache.has('modelsCustomData')) {
            return Promise.resolve(sceneCustomDataCache.get('modelsCustomData'));
        }

        // if GLB mode there is no need to call the /unzip endpoint
        if (app.isGlbFileMode) {
            sceneCustomDataCache.set('modelsCustomData', {
                materials: [],
                textures: [],
                images: [{
                doubleSided: true,
                name: new Date().getTime(),
                pbrMetallicRoughness: {},
                extensions: {
                    KHR_materials_transmission: {transmissionFactor: 0.5}
                }
                }]
            });
            return Promise.resolve(sceneCustomDataCache.get('modelsCustomData'));
        }

        return getMaterialData(queryParams.fileOid, queryParams.env).then((zipFileResponse) => {
            const images = [];
            const textures = [];
            const materials = [{
                doubleSided: true,
                name: new Date().getTime(),
                pbrMetallicRoughness: {},
                extensions: {
                    KHR_materials_transmission: {transmissionFactor: 0.5}
                }
            }];

            const cmsInInch = 2.54;
            let hadBaseAlphaTexture = false;

            zipFileResponse.forEach((texture, index) => {
                images.push({uri: texture.url});
                textures.push({sampler: 0, source: index});

                const width = texture.imageSize.width / (texture.imageSize.pixelInCmX / cmsInInch);
                const height = texture.imageSize.height / (texture.imageSize.pixelInCmY / cmsInInch);
                const extensions = {KHR_texture_transform: {scale: [1 / width, 1 / height]}};

                if (texture.name === BASE_ALPHA_TEXTURE_NAME) {
                    hadBaseAlphaTexture = true;
                    materials[0].pbrMetallicRoughness.baseColorTexture = {
                      index,
                      extensions,
                    };
                    materials[0].pbrMetallicRoughness.baseColorFactor = [
                        0.800000011920929,
                        0.800000011920929,
                        0.800000011920929,
                        1
                    ];
                    materials[0].pbrMetallicRoughness.roughnessFactor = 0.5;
                    materials[0].pbrMetallicRoughness.metallicFactor = 1.0;
                    materials[0].alphaMode = "MASK";
                    materials[0].alphaCutoff = 0.5;

                    materials[0].doubleSided = false;
                } else if (isBaseMapping(texture.name) && !hadBaseAlphaTexture) {
                    materials[0].pbrMetallicRoughness.baseColorTexture = {index, extensions};
                } else if (isNormalMapping(texture.name)) {
                    materials[0].normalTexture = {index, extensions};
                } else if (isAmbientOcclusionMapping(texture.name)) {
                    materials[0].occlusionTexture = {index, extensions};
                } else if (texture.name === METALLIC_ROUGHNESS_TEXTURE_NAME) {
                    materials[0].pbrMetallicRoughness.metallicRoughnessTexture = {index, extensions};
                }
            });

            // Use the updated updatePathProvider method
            if (hadBaseAlphaTexture) {
                uiModel.updatePathProvider(alphaPathProvider);
                app.setHideModelSelector(true);
            }

            sceneCustomDataCache.set('modelsCustomData', {
                materials: materials,
                textures: textures,
                images: images
            });

            return sceneCustomDataCache.get('modelsCustomData');
        }).catch((error) => {
            if (isNetworkError(error.message)) {
                return Promise.reject('Connection error. Please try again later.');
            }

            return Promise.reject(error);
        });
    }

    uiModel.disabledAnimations(uiModel.activeAnimations.pipe(map(animationIndices => {
        // Disable all animations which are not disjoint to the current selection of animations.
        return state.gltf.nonDisjointAnimations(animationIndices);
    })));

    const sceneChangedSubject = new Subject();
    const sceneChangedObservable = uiModel.scene.pipe(map(newSceneIndex => {
            state.sceneIndex = newSceneIndex;
            state.cameraIndex = undefined;
            state.userCameraIndex = 1;
            const scene = state.gltf.scenes[state.sceneIndex];
            if (scene !== undefined) {
                scene.applyTransformHierarchy(state.gltf);
                state.userCamera.fitViewToScene(state.gltf, state.sceneIndex, state.userCameraIndex);
            }
        }),
        multicast(sceneChangedSubject)
    );

    const statisticsUpdateObservableTemp = merge(
        gltfLoadedMulticast,
        sceneChangedObservable
    );

    const statisticsUpdateObservable = statisticsUpdateObservableTemp.pipe(
        map((_) => view.gatherStatistics(state))
    );

    const cameraExportChangedObservable = uiModel.cameraValuesExport.pipe(map(_ => {
        let camera = state.userCamera;
        if (state.cameraIndex !== undefined) {
            camera = state.gltf.cameras[state.cameraIndex];
        }
        const cameraDesc = camera.getDescription(state.gltf);
        return cameraDesc;
    }));

    const downloadDataURL = (filename, dataURL) => {
        var element = document.createElement('a');
        element.setAttribute('href', dataURL);
        element.setAttribute('download', filename);

        element.style.display = 'none';
        document.body.appendChild(element);

        element.click();

        document.body.removeChild(element);
    };

    cameraExportChangedObservable.subscribe(cameraDesc => {
        const gltf = JSON.stringify(cameraDesc, undefined, 4);
        const dataURL = 'data:text/plain;charset=utf-8,' + encodeURIComponent(gltf);
        downloadDataURL("camera.gltf", dataURL);
    });

    uiModel.captureCanvas.subscribe(() => {
        view.renderFrame(state, canvas.width, canvas.height);
        const dataURL = canvas.toDataURL();
        downloadDataURL("capture.png", dataURL);
    });

    // Only redraw glTF view upon user inputs, or when an animation is playing.
    let redraw = false;
    const listenForRedraw = stream => stream.subscribe(() => redraw = true);

    uiModel.scene.pipe(filter(scene => scene === -1)).subscribe(() => {
        state.sceneIndex = undefined;
    });
    uiModel.scene.pipe(filter(scene => scene !== -1)).subscribe(scene => {
        state.sceneIndex = scene;
    });
    listenForRedraw(uiModel.scene);

    uiModel.camera.pipe(filter(camera => camera === -1)).subscribe(() => {
        state.cameraIndex = undefined;
    });
    uiModel.camera.pipe(filter(camera => camera !== -1)).subscribe(camera => {
        state.cameraIndex = camera;
    });
    listenForRedraw(uiModel.camera);

    uiModel.variant.subscribe(variant => {
        state.variant = variant;
    });
    listenForRedraw(uiModel.variant);

    uiModel.tonemap.subscribe(tonemap => {
        state.renderingParameters.toneMap = tonemap;
    });
    listenForRedraw(uiModel.tonemap);

    uiModel.debugchannel.subscribe(debugchannel => {
        state.renderingParameters.debugOutput = debugchannel;
    });
    listenForRedraw(uiModel.debugchannel);

    uiModel.skinningEnabled.subscribe(skinningEnabled => {
        state.renderingParameters.skinning = skinningEnabled;
    });
    listenForRedraw(uiModel.skinningEnabled);

    uiModel.exposure.subscribe(exposure => {
        state.renderingParameters.exposure = (1.0 / Math.pow(2.0, exposure));
    });
    listenForRedraw(uiModel.exposure);

    uiModel.morphingEnabled.subscribe(morphingEnabled => {
        state.renderingParameters.morphing = morphingEnabled;
    });
    listenForRedraw(uiModel.morphingEnabled);

    uiModel.clearcoatEnabled.subscribe(clearcoatEnabled => {
        state.renderingParameters.enabledExtensions.KHR_materials_clearcoat = clearcoatEnabled;
    });
    uiModel.sheenEnabled.subscribe(sheenEnabled => {
        state.renderingParameters.enabledExtensions.KHR_materials_sheen = sheenEnabled;
    });
    uiModel.transmissionEnabled.subscribe(transmissionEnabled => {
        state.renderingParameters.enabledExtensions.KHR_materials_transmission = transmissionEnabled;
    });
    uiModel.volumeEnabled.subscribe(volumeEnabled => {
        state.renderingParameters.enabledExtensions.KHR_materials_volume = volumeEnabled;
    });
    uiModel.iorEnabled.subscribe(iorEnabled => {
        state.renderingParameters.enabledExtensions.KHR_materials_ior = iorEnabled;
    });
    uiModel.iridescenceEnabled.subscribe(iridescenceEnabled => {
        state.renderingParameters.enabledExtensions.KHR_materials_iridescence = iridescenceEnabled;
    });
    uiModel.specularEnabled.subscribe(specularEnabled => {
        state.renderingParameters.enabledExtensions.KHR_materials_specular = specularEnabled;
    });
    uiModel.emissiveStrengthEnabled.subscribe(enabled => {
        state.renderingParameters.enabledExtensions.KHR_materials_emissive_strength = enabled;
    });
    listenForRedraw(uiModel.clearcoatEnabled);
    listenForRedraw(uiModel.sheenEnabled);
    listenForRedraw(uiModel.transmissionEnabled);
    listenForRedraw(uiModel.volumeEnabled);
    listenForRedraw(uiModel.iorEnabled);
    listenForRedraw(uiModel.specularEnabled);
    listenForRedraw(uiModel.iridescenceEnabled);
    listenForRedraw(uiModel.emissiveStrengthEnabled);

    uiModel.iblEnabled.subscribe(iblEnabled => {
        state.renderingParameters.useIBL = iblEnabled;
    });
    listenForRedraw(uiModel.iblEnabled);

    uiModel.iblIntensity.subscribe(iblIntensity => {
        state.renderingParameters.iblIntensity = Math.pow(10, iblIntensity);
    });
    listenForRedraw(uiModel.iblIntensity);

    uiModel.renderEnvEnabled.subscribe(renderEnvEnabled => {
        state.renderingParameters.renderEnvironmentMap = renderEnvEnabled;
    });
    uiModel.blurEnvEnabled.subscribe(blurEnvEnabled => {
        state.renderingParameters.blurEnvironmentMap = blurEnvEnabled;
    });
    listenForRedraw(uiModel.renderEnvEnabled);
    listenForRedraw(uiModel.blurEnvEnabled);

    uiModel.punctualLightsEnabled.subscribe(punctualLightsEnabled => {
        state.renderingParameters.usePunctual = punctualLightsEnabled;
    });
    listenForRedraw(uiModel.punctualLightsEnabled);

    uiModel.environmentRotation.subscribe(environmentRotation => {
        switch (environmentRotation) {
        case "+Z":
            state.renderingParameters.environmentRotation = 90.0;
            break;
        case "-X":
            state.renderingParameters.environmentRotation = 180.0;
            break;
        case "-Z":
            state.renderingParameters.environmentRotation = 270.0;
            break;
        case "+X":
            state.renderingParameters.environmentRotation = 0.0;
            break;
        }
    });
    listenForRedraw(uiModel.environmentRotation);


    uiModel.clearColor.subscribe(clearColor => {
        state.renderingParameters.clearColor = clearColor;
    });
    listenForRedraw(uiModel.clearColor);

    uiModel.animationPlay.subscribe(animationPlay => {
        if (animationPlay) {
            state.animationTimer.unpause();
        } else {
            state.animationTimer.pause();
        }
    });

    uiModel.activeAnimations.subscribe(animations => {
        state.animationIndices = animations;
    });
    listenForRedraw(uiModel.activeAnimations);

    uiModel.hdr.subscribe(hdrFile => {
        resourceLoader.loadEnvironment(hdrFile).then((environment) => {
            state.environment = environment;
            //We neeed to wait until the environment is loaded to redraw
            redraw = true;
        });
    });

    uiModel.attachGltfLoaded(gltfLoadedMulticast);
    uiModel.updateStatistics(statisticsUpdateObservable);
    const sceneChangedStateObservable = uiModel.scene.pipe(map(newSceneIndex => state));
    uiModel.attachCameraChangeObservable(sceneChangedStateObservable);
    gltfLoadedMulticast.connect();

    uiModel.orbit.subscribe(orbit => {
        if (state.cameraIndex === undefined) {
            state.userCamera.orbit(orbit.deltaPhi, orbit.deltaTheta);
        }
    });
    listenForRedraw(uiModel.orbit);

    uiModel.pan.subscribe(pan => {
        if (state.cameraIndex === undefined) {
            state.userCamera.pan(pan.deltaX, -pan.deltaY);
        }
    });
    listenForRedraw(uiModel.pan);

    uiModel.zoom.subscribe(zoom => {
        if (state.cameraIndex === undefined) {
            state.userCamera.zoomBy(zoom.deltaZoom);
        }
    });
    listenForRedraw(uiModel.zoom);

    // configure the animation loop
    const past = {};
    const update = () => {
        const devicePixelRatio = window.devicePixelRatio || 1;

        // set the size of the drawingBuffer based on the size it's displayed.
        canvas.width = Math.floor(canvas.clientWidth * devicePixelRatio);
        canvas.height = Math.floor(canvas.clientHeight * devicePixelRatio);
        redraw |= !state.animationTimer.paused && state.animationIndices.length > 0;
        redraw |= past.width != canvas.width || past.height != canvas.height;
        past.width = canvas.width;
        past.height = canvas.height;

        if (redraw) {
            view.renderFrame(state, canvas.width, canvas.height);
            redraw = false;
        }

        window.requestAnimationFrame(update);
    };

    // After this start executing animation loop.
    window.requestAnimationFrame(update);
}

function isBaseMapping(textureName) {
    return textureName.toLowerCase().includes(BASE_MAPPING_NAMES[0]) ||
        textureName.toLowerCase().includes(BASE_MAPPING_NAMES[1]) ||
        textureName.toLowerCase().includes(BASE_MAPPING_NAMES[2]) ||
        textureName.toLowerCase().includes(BASE_MAPPING_NAMES[3]) ||
        textureName.toLowerCase().includes(BASE_MAPPING_NAMES[4]);
}

function isNormalMapping(textureName) {
    return textureName.toLowerCase().includes(NORMAL_MAPPING_NAMES[0]) ||
        textureName.toLowerCase().includes(NORMAL_MAPPING_NAMES[1]) ||
        textureName.toLowerCase().includes(NORMAL_MAPPING_NAMES[2]);
}

function isAmbientOcclusionMapping(textureName) {
    return textureName.toLowerCase().includes(AMBIENT_MAPPING_NAMES[0]) ||
        textureName.toLowerCase().includes(AMBIENT_MAPPING_NAMES[1]) ||
        textureName.toLowerCase().includes(AMBIENT_MAPPING_NAMES[2]);
}

async function getMaterialData(fileOid, env) {
    let url = '';
    if (env) {
        url = `${ENVIRONMENT_VARIABLES['apiUrl']}/unzip?fileOid=${fileOid}&env=${env}`;
    } else {
        url = `${ENVIRONMENT_VARIABLES['apiUrl']}/unzip?fileOid=${fileOid}`;
    }

    let response;

    try {
        response = await fetch(url);
    } catch (error) {
        return Promise.reject('Something went wrong. Please try later');
    }

    const reader = response.body.getReader();
    const contentLength = +response.headers.get('Content-Length');

    let receivedLength = 0;
    let chunks = [];

    while (true) {
        let readableStream = null;

        try {
            readableStream = await reader.read();
        } catch (e) {
            return Promise.reject(e);
        }

        const done = readableStream.done;
        const value = readableStream.value;

        if (done) {
            break;
        }

        chunks.push(value);
        receivedLength += value.length;
        app.progress = Math.floor(receivedLength * 100 / contentLength);
    }

    let chunksAll = new Uint8Array(receivedLength);
    let position = 0;
    for (let chunk of chunks) {
        chunksAll.set(chunk, position);
        position += chunk.length;
    }

    let result = new TextDecoder("utf-8").decode(chunksAll);
    let commits = JSON.parse(result);

    return response.status === 200 ? commits : Promise.reject(response);
}

async function getGlbFile(fileOid, env) {
    let url = '';
    if (env) {
        url = `${ENVIRONMENT_VARIABLES['apiUrl']}/glb?fileOid=${fileOid}&env=${env}`;
    } else {
        url = `${ENVIRONMENT_VARIABLES['apiUrl']}/glb?fileOid=${fileOid}`;
    }

    let response;

    try {
        response = await fetch(url);
    } catch (error) {
        return Promise.reject('Something went wrong. Please try later');
    }

    const reader = response.body.getReader();
    const contentLength = +response.headers.get('Content-Length');

    let receivedLength = 0;
    let chunks = [];

    while (true) {
        const {done, value} = await reader.read();

        if (done) {
            break;
        }

        chunks.push(value);
        receivedLength += value.length;
        app.progress = Math.floor(receivedLength * 100 / contentLength);
    }

    let chunksAll = new Uint8Array(receivedLength);
    let position = 0;
    for (let chunk of chunks) {
        chunksAll.set(chunk, position);
        position += chunk.length;
    }

    return response.status === 200 ? chunksAll.buffer : Promise.reject(response);
}

function isGlbFileMode(queryParams) {
    return !!queryParams.isglb;
}

function getQueryParams() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    const queryParams = {
        fileOid: '',
        fileName: '',
        env: false
    };

    for (let param of urlParams.entries()) {
        queryParams[param[0]] = param[1];
    }

    return queryParams;
}

export {main};